import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorDisplay';
import { UpdateNotification } from './components/AppUpdate';
import { useAppUpdate } from './hooks/useAppUpdate';
import { initDeviceTracking } from './utils/deviceTracking';
import Layout from './components/Layout';
import NetworkStatus from './components/NetworkStatus';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Barbers from './pages/Barbers';
import Revenues from './pages/Revenues';
import Expenses from './pages/Expenses';
import Invoices from './pages/Invoices';
import Reports from './pages/Reports';
import Appointments from './pages/Appointments';
import Inventory from './pages/Inventory';
import Salaries from './pages/Salaries';
import Settings from './pages/Settings';
import GuestBooking from './pages/GuestBooking';
import QueuePage from './pages/QueuePage';
import MyRevenues from './pages/MyRevenues';
import EmailManagement from './pages/EmailManagement';
import PublicBooking from './pages/PublicBooking';
import BookingSuccess from './pages/BookingSuccess';
import PublicRating from './pages/PublicRating';
import Analytics from './pages/Analytics';
import FixedCommitments from './pages/FixedCommitments';
import DailyReport from './pages/Admin/DailyReport';
import WhatsAppSettings from './pages/Admin/WhatsAppSettings';
import WeeklyReport from './pages/Admin/WeeklyReport';
import BarberReports from './pages/BarberReports';
import CustomerInsights from './pages/CustomerInsights';
import RentSimulator from './pages/RentSimulator';
import EmployeeHousing from './pages/EmployeeHousing';
import ComprehensiveReports from './pages/ComprehensiveReports';
import Ratings from './pages/Ratings';
import NotificationSettings from './pages/Admin/NotificationSettings';
import ActivityLog from './pages/Admin/ActivityLog';
import ServicesManagement from './pages/Admin/ServicesManagement';
import PopupNotificationsManagement from './pages/Admin/PopupNotificationsManagement';
import UpdateManagement from './pages/Admin/UpdateManagement';
import AppStatistics from './pages/Admin/AppStatistics';
import DeletedUsers from './pages/Admin/DeletedUsers';
import RevenueStats from './pages/Admin/RevenueStats';
import BarberViolations from './pages/Admin/BarberViolations';
import DataBackup from './pages/Admin/DataBackup';
import DetailedReports from './pages/Admin/DetailedReports';
import PermissionsLogs from './pages/Admin/PermissionsLogs';
import UserGuide from './pages/UserGuide';
import PermissionsManagement from './pages/PermissionsManagement';
import Customers from './pages/Customers';
import LeavesPermissions from './pages/LeavesPermissions';
import LoyaltyAdmin from './pages/LoyaltyAdmin';
import WeeklyLoyaltyReports from './pages/Admin/WeeklyReports';
import LoyaltyPublic from './pages/LoyaltyPublic';
import './i18n/config';
import './App.css';

// Register Service Worker
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
          
          // Check for updates every 5 minutes
          setInterval(() => {
            registration.update();
          }, 5 * 60 * 1000);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    });
  }
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, isGuest } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect guest to booking page
  if (isGuest() && window.location.pathname === '/dashboard') {
    return <Navigate to="/booking" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  // Use the auto-update hook with hourly checks
  const { 
    updateAvailable, 
    updateInfo, 
    applyUpdate, 
    dismissUpdate,
    isForceUpdate,
    lastChecked 
  } = useAppUpdate({
    autoCheck: true,
    checkInterval: 60 * 60 * 1000, // 1 hour
    initialDelay: 10 * 1000, // 10 seconds after app load
    onUpdateAvailable: (info) => {
      console.log('📱 Update detected:', info.version);
    }
  });

  useEffect(() => {
    // Register service worker
    registerServiceWorker();
    
    // Initialize device tracking for statistics
    initDeviceTracking();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <NetworkStatus />
            {/* Auto-update notification with enhanced features */}
            <UpdateNotification 
              show={updateAvailable}
              updateInfo={updateInfo}
              onDismiss={dismissUpdate}
              onUpdate={applyUpdate}
              isForceUpdate={isForceUpdate}
            />
            <Toaster 
              position="top-center"
              theme="dark"
              toastOptions={{
                style: {
                  background: '#18181b',
                  color: '#fafafa',
                  border: '1px solid rgba(255,255,255,0.1)',
                },
              }}
            />
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<Navigate to="/#services-section" replace />} />
            <Route path="/book" element={<Navigate to="/public-booking" replace />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/public-booking" element={<PublicBooking />} />
            <Route path="/booking/success" element={<BookingSuccess />} />
            <Route path="/rate-us" element={<PublicRating />} />
            <Route path="/loyalty" element={<LoyaltyPublic />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/barbers" element={<ProtectedRoute><Barbers /></ProtectedRoute>} />
            <Route path="/revenues" element={<ProtectedRoute><Revenues /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
            <Route path="/salaries" element={<ProtectedRoute><Salaries /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/booking" element={<ProtectedRoute><GuestBooking /></ProtectedRoute>} />
            <Route path="/queue" element={<ProtectedRoute><QueuePage /></ProtectedRoute>} />
            <Route path="/my-revenues" element={<ProtectedRoute><MyRevenues /></ProtectedRoute>} />
            <Route path="/emails" element={<ProtectedRoute><EmailManagement /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/fixed-commitments" element={<ProtectedRoute><FixedCommitments /></ProtectedRoute>} />
            <Route path="/daily-report" element={<ProtectedRoute><DailyReport /></ProtectedRoute>} />
            <Route path="/whatsapp-settings" element={<ProtectedRoute><WhatsAppSettings /></ProtectedRoute>} />
            <Route path="/barber-reports" element={<ProtectedRoute><BarberReports /></ProtectedRoute>} />
            <Route path="/customer-insights" element={<ProtectedRoute><CustomerInsights /></ProtectedRoute>} />
            <Route path="/rent-simulator" element={<ProtectedRoute><RentSimulator /></ProtectedRoute>} />
            <Route path="/employee-housing" element={<ProtectedRoute><EmployeeHousing /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
            <Route path="/leaves" element={<ProtectedRoute><LeavesPermissions /></ProtectedRoute>} />
            <Route path="/loyalty-admin" element={<ProtectedRoute><LoyaltyAdmin /></ProtectedRoute>} />
            <Route path="/weekly-loyalty-reports" element={<ProtectedRoute><WeeklyLoyaltyReports /></ProtectedRoute>} />
            <Route path="/comprehensive-reports" element={<ProtectedRoute><ComprehensiveReports /></ProtectedRoute>} />
            <Route path="/ratings" element={<ProtectedRoute><Ratings /></ProtectedRoute>} />
            <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
            <Route path="/weekly-report" element={<ProtectedRoute><WeeklyReport /></ProtectedRoute>} />
            <Route path="/weekly-report/:week_start" element={<ProtectedRoute><WeeklyReport /></ProtectedRoute>} />
            <Route path="/activity-log" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
            <Route path="/user-guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
            <Route path="/permissions" element={<ProtectedRoute><PermissionsManagement /></ProtectedRoute>} />
            <Route path="/services-management" element={<ProtectedRoute><ServicesManagement /></ProtectedRoute>} />
            <Route path="/popup-notifications" element={<ProtectedRoute><PopupNotificationsManagement /></ProtectedRoute>} />
            <Route path="/update-management" element={<ProtectedRoute><UpdateManagement /></ProtectedRoute>} />
            <Route path="/app-statistics" element={<ProtectedRoute><AppStatistics /></ProtectedRoute>} />
            <Route path="/deleted-users" element={<ProtectedRoute><DeletedUsers /></ProtectedRoute>} />
            <Route path="/revenue-stats" element={<ProtectedRoute><RevenueStats /></ProtectedRoute>} />
            <Route path="/barber-violations" element={<ProtectedRoute><BarberViolations /></ProtectedRoute>} />
            <Route path="/data-backup" element={<ProtectedRoute><DataBackup /></ProtectedRoute>} />
            <Route path="/detailed-reports" element={<ProtectedRoute><DetailedReports /></ProtectedRoute>} />
            <Route path="/permissions-logs" element={<ProtectedRoute><PermissionsLogs /></ProtectedRoute>} />
          </Routes>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
